import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../store/store";
import { fetchDroneById, selectSelectedDrone, selectDronesLoading, selectDronesError } from "../../store/drone_slice";
import DroneGallery from "./drone_gallery";
import DroneInfo from "./drone_info";
import DroneSpecs from "./drone_specs";
import OwnerInfo from "./owner_info";
import StaticMap from "../../common/google_map/static_map";
import Header from "../../common/header/header";
import Footer from "../../common/footer/footer";
import SkeletonLoader from "../../common/skeleton/skeleton_loader";

const DroneProductPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const droneData = useSelector(selectSelectedDrone);
  const loading = useSelector(selectDronesLoading);
  const error = useSelector(selectDronesError);
  
  // Use all imageKeys from the API response
  const imageKeys = droneData?.imageKeys || [];

  useEffect(() => {
    if (id) {
      const numericId = parseInt(id, 10);
      if (!isNaN(numericId)) {
        dispatch(fetchDroneById(numericId));
      } else {
        console.error("Invalid drone ID:", id);
      }
    }
  }, [dispatch, id]);

  if (loading) {
    return <SkeletonLoader type="product" />;
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col">
        <div className="flex-grow w-full flex justify-center">
          <div className="w-full max-w-7xl p-4">
            <Header />
            <div className="py-20 text-center text-red-500">Error loading drone: {error}</div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!droneData) {
    return (
      <div className="min-h-screen flex flex-col">
        <div className="flex-grow w-full flex justify-center">
          <div className="w-full max-w-7xl p-4">
            <Header />
            <div className="py-20 text-center">Drone not found.</div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-grow w-full flex justify-center">
        <div className="w-full max-w-7xl p-4">
          <Header />
          <div className="flex flex-col md:flex-row py-6">
            <div className="md:w-2/3 pr-4">
              {imageKeys.length > 0 && <DroneGallery imageKeys={imageKeys} />}
              <DroneSpecs specs={droneData.specs} extraGears={droneData.extraGears} />
              <div className="mt-6">
                <h3 className="text-xl font-semibold mb-2">Location</h3>
                <StaticMap lat={droneData.lat} lng={droneData.lng} />
              </div>
            </div>
            <div className="md:w-1/3">
              <DroneInfo droneData={droneData} />
              {droneData.postedBy && (
                <OwnerInfo 
                  owner={droneData.postedBy} 
                  creationYear={droneData.dateTime?.split('-')[0] || undefined}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default DroneProductPage;
